"use client"

import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import type { LucideIcon } from "lucide-react"
import {
  ArrowUpRight,
  ChevronDown,
  Hash,
  Menu,
  Pause,
  PhoneForwarded,
  PhoneOff,
  UserRound,
  Voicemail,
} from "lucide-react"
import Link from "next/link"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { TelephoneKeypad } from "@/components/telephone-keypad"
import {
  NowCallingPopoverHandle,
  handleColumnButtonClass,
  handleColumnClass,
  keypadPanelWidthClass,
  nowCallingPopoverContentClass,
  nowCallingSurfaceClass,
} from "@/components/now-calling/now-calling-popover-handle"
import { NowCallingTransferButton } from "@/components/now-calling/now-calling-transfer-button"
import {
  CallResultDialog,
  type CallResultDialogSnapshot,
} from "@/components/now-calling/call-result-dialog"
import { formatPhoneForDisplay } from "@/components/crm/contact/contact-edit-form"
import type { NowCallingTransferRequest } from "@/components/now-calling/transfer-destinations"
import { useActiveCall, type ActiveCallControlAction, type ActiveCallSnapshot } from "@/lib/active-call"
import { CommandPage } from "@/lib/command"
import { formatContactListInstant } from "@/lib/datetime/utc-local-display"
import {
  DIAL_DISPOSITION_GRID,
  DIAL_DISPOSITION_GROUPS,
  type DialDispositionId,
} from "@/lib/list-dialer/dispositions"
import { cn } from "@/lib/utils"

const CONTACT_COMMAND_PATH = "/app/crm/base/cmd/contact"

/** When true (default), shows the secondary call-details row below the main bar controls. */
export const DEFAULT_SHOW_CALL_DETAILS_ROW = true

export type NowCallingCallLabelVariant =
  | "inbound"
  | "outbound"
  | "list-dial"
  | "new-lead"
  | "appointment"
  | "follow-up"
  | "default"

export type NowCallingCallLabel = {
  text: string
  variant?: NowCallingCallLabelVariant
}

export type NowCallingCallDetails = {
  callLabel: NowCallingCallLabel
  optionalInfo?: string
  callResultMode: "prompt" | "select"
  callResultOptions?: string[]
  callResultValue?: string
}

export type NowCallingOverlayProps = {
  showCallDetailsRow?: boolean
  callDetails?: NowCallingCallDetails
}

const callLabelClass: Record<NowCallingCallLabelVariant, string> = {
  inbound: "bg-sky-100 text-sky-800 ring-sky-200",
  outbound: "bg-orange-100 text-orange-800 ring-orange-200",
  "list-dial": "bg-teal-100 text-teal-800 ring-teal-200",
  "new-lead": "bg-emerald-100 text-emerald-800 ring-emerald-200",
  appointment: "bg-violet-100 text-violet-800 ring-violet-200",
  "follow-up": "bg-amber-100 text-amber-800 ring-amber-200",
  default: "bg-slate-100 text-slate-700 ring-slate-200",
}

const defaultCallDetails: NowCallingCallDetails = {
  callLabel: { text: "Inbound", variant: "inbound" },
  optionalInfo: "at 12:30 PM",
  callResultMode: "select",
  callResultOptions: DIAL_DISPOSITION_GROUPS.flatMap((group) =>
    group.options.map((option) => option.label),
  ),
  callResultValue: "",
}

function callLabelFromSnapshot(call: ActiveCallSnapshot): NowCallingCallLabel {
  const requestType = (
    call.system_request_type ??
    call.system_call?.request_type ??
    ""
  )
    .toString()
    .toLowerCase()

  if (requestType === "list_dial" || call.call_mode === "list_dial") {
    return { text: "List Dialer", variant: "list-dial" }
  }
  if (requestType === "lead_queue") {
    return { text: "New Lead", variant: "new-lead" }
  }
  if (requestType === "appointment") {
    return { text: "Appointment", variant: "appointment" }
  }
  if (requestType === "followup") {
    return { text: "Follow-up", variant: "follow-up" }
  }

  const inbound = call.call_direction === "inbound"
  return {
    text: inbound ? "Inbound" : "Outbound",
    variant: inbound ? "inbound" : "outbound",
  }
}

function callDetailsFromActiveCall(call: ActiveCallSnapshot): NowCallingCallDetails {
  const startedAt = call.started_at ? formatContactListInstant(call.started_at) : ""
  const connecting = call.lifecycle_status === "connecting"

  return {
    ...defaultCallDetails,
    callLabel: callLabelFromSnapshot(call),
    optionalInfo: connecting
      ? "Connecting…"
      : startedAt
        ? `at ${startedAt}`
        : undefined,
  }
}

/** Default horizontal position (% of viewport width). */
const DEFAULT_EXPANDED_CENTER_PERCENT = 50
const DEFAULT_COLLAPSED_LEFT_PERCENT = 20

/** Uniform spacing scale (12px) for padding and gaps. */
const barPaddingNoRight = "pt-3 pb-3 pl-3 pr-0"
const barPaddingCollapsed = "pt-3 pl-3 pr-0 pb-0"
const barGap = "gap-3"

const OVERLAY_PADDING_PX = 24

/** Expanded bar width without list-dial Drop Voicemail (original layout). */
const EXPANDED_BAR_WIDTH_CLASS = "w-[46rem]"
/**
 * Wider bar when Drop Voicemail is shown: adds one action column at the same
 * per-button width as the 5-button layout (46rem inner pool, 1fr:2.4fr split).
 */
const EXPANDED_BAR_WIDTH_LIST_DIAL_CLASS = "w-[54.5rem]"

const handleGridColsCollapsed =
  "[grid-template-columns:minmax(11rem,auto)_calc(1.5cm*0.67)]"
const handleGridColsExpanded =
  "[grid-template-columns:minmax(11rem,1fr)_minmax(0,2.4fr)_calc(1.5cm*0.67)]"

const actionButtonClass =
  "flex h-full min-h-12 w-full flex-col items-center justify-center gap-1 rounded-sm border bg-gradient-to-b px-3 py-3 text-xs font-semibold shadow-[inset_0_1px_0_rgb(255_255_255/0.65),0_2px_0_rgb(15_23_42/0.18)] transition-all hover:brightness-105 active:translate-y-px active:shadow-[inset_0_2px_4px_rgb(15_23_42/0.2)]"

const callingActions: {
  label: string
  icon?: LucideIcon
  park?: boolean
  keypad?: boolean
  transfer?: boolean
  className: string
}[] = [
  {
    label: "Keypad",
    icon: Hash,
    keypad: true,
    className:
      "border-emerald-300 from-emerald-50 via-emerald-100 to-emerald-200 text-emerald-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(167_243_208/0.6)]",
  },
  {
    label: "Transfer",
    icon: PhoneForwarded,
    transfer: true,
    className:
      "border-blue-300 from-sky-50 via-sky-100 to-blue-200 text-blue-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(191_219_254/0.6)]",
  },
  {
    label: "Hold",
    icon: Pause,
    className:
      "border-amber-300 from-amber-50 via-amber-100 to-amber-200 text-amber-700 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(253_230_138/0.6)]",
  },
  {
    label: "Park",
    park: true,
    className:
      "border-violet-300 from-violet-50 via-violet-100 to-violet-200 text-violet-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(221_214_254/0.6)]",
  },
  {
    label: "Hang Up",
    icon: PhoneOff,
    className:
      "border-red-300 from-red-50 via-red-100 to-red-200 text-red-600 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(254_202_202/0.6)]",
  },
]

/** Expanded: bar center as % of viewport width (50 = centered). */
function clampExpandedCenterPercent(percent: number, barWidth: number) {
  const vw = window.innerWidth
  if (vw <= 0) return percent
  const min = ((barWidth / 2 + OVERLAY_PADDING_PX) / vw) * 100
  const max = ((vw - barWidth / 2 - OVERLAY_PADDING_PX) / vw) * 100
  return Math.min(max, Math.max(min, percent))
}

/** Collapsed: bar left edge as % of viewport width. */
function clampCollapsedLeftPercent(percent: number, barWidth: number) {
  const vw = window.innerWidth
  if (vw <= 0) return percent
  const min = 0
  const max = ((vw - barWidth) / vw) * 100
  return Math.min(max, Math.max(min, percent))
}

/** Half the height of the main control row (main row uses min-h-12). */
const callDetailsRowClass = "min-h-6"

function CallDescriptionLabels({
  callLabel,
  optionalInfo,
}: Pick<NowCallingCallDetails, "callLabel" | "optionalInfo">) {
  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs leading-snug text-slate-700">
      <span
        className={cn(
          "inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
          callLabelClass[callLabel.variant ?? "default"]
        )}
      >
        {callLabel.text}
      </span>
      {optionalInfo ? (
        <span className="text-slate-600">
          <span className="text-slate-400">·</span> {optionalInfo}
        </span>
      ) : null}
    </p>
  )
}

function dispositionLabel(value: string): string {
  return DIAL_DISPOSITION_GRID.find((option) => option.value === value)?.label ?? value
}

function CallResultField({
  mode,
  value,
  error,
  onChange,
}: {
  mode: NowCallingCallDetails["callResultMode"]
  value: DialDispositionId | ""
  error?: string | null
  onChange: (value: DialDispositionId | "") => void
}) {
  const [open, setOpen] = useState(false)

  if (mode === "prompt") {
    return (
      <div className="flex min-w-0 items-center gap-1.5 text-xs">
        <span className="shrink-0 font-bold text-slate-700">Result:</span>
        <span className="italic text-slate-500">Prompt</span>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs">
      <span className="shrink-0 font-bold text-slate-700">Result:</span>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Call result"
            className={cn(
              "flex min-w-0 flex-1 items-center justify-between gap-1 rounded-sm border border-slate-300 bg-white px-2 py-1 text-left text-xs font-medium text-slate-800 shadow-sm outline-none",
              "transition-colors hover:border-slate-400 hover:bg-slate-50",
              "focus-visible:border-slate-400 focus-visible:ring-2 focus-visible:ring-slate-300/60",
              "data-[state=open]:border-slate-400 data-[state=open]:ring-2 data-[state=open]:ring-slate-300/60"
            )}
          >
            <span className="truncate" title={error ?? undefined}>
              {value ? dispositionLabel(value) : "Select result"}
            </span>
            <ChevronDown className="size-3.5 shrink-0 text-slate-500 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="top"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            "z-[100] w-[28rem] overflow-hidden rounded-md border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100 p-2 shadow-[0_10px_38px_rgb(15_23_42/0.18)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=top]:slide-in-from-bottom-2"
          )}
        >
          <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Call disposition">
            {DIAL_DISPOSITION_GROUPS.map((group) => (
              <div
                key={group.id}
                role="group"
                className="flex w-full flex-nowrap items-stretch gap-1.5"
              >
                {group.options.map(({ label, value: optionValue }) => {
                  const selected = value === optionValue
                  return (
                    <button
                      key={optionValue}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      title={label}
                      aria-label={label}
                      onClick={() => {
                        onChange(selected ? "" : optionValue)
                        setOpen(false)
                      }}
                      className={cn(
                        "flex h-8 min-w-0 flex-1 basis-0 items-center justify-center rounded-sm border px-1 text-center text-[11px] font-semibold leading-tight transition-all",
                        "shadow-[inset_0_1px_0_rgb(255_255_255/0.7),0_1px_0_rgb(15_23_42/0.08)]",
                        selected
                          ? "border-slate-400 bg-gradient-to-b from-slate-200 to-slate-300 text-slate-900 ring-1 ring-slate-400/50"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:from-slate-50 hover:to-slate-100 hover:bg-gradient-to-b"
                      )}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      {error ? (
        <span className="max-w-[8rem] truncate text-[10px] font-medium text-red-600" title={error}>
          {error}
        </span>
      ) : null}
    </div>
  )
}

function CallDetailsRow({
  details,
  callResult,
  resultError,
  onCallResultChange,
}: {
  details: NowCallingCallDetails
  callResult: DialDispositionId | ""
  resultError?: string | null
  onCallResultChange: (value: DialDispositionId | "") => void
}) {
  const hideResult = details.callLabel.variant === "list-dial"

  return (
    <div
      className={cn(
        "col-span-2 col-start-1 row-start-2 grid w-full items-center gap-3 border-t border-slate-300/90",
        hideResult ? "grid-cols-1" : "grid-cols-[2fr_1fr]",
        callDetailsRowClass,
        "pt-2"
      )}
    >
      <CallDescriptionLabels {...details} />
      {hideResult ? null : (
        <CallResultField
          mode={details.callResultMode}
          value={callResult}
          error={resultError}
          onChange={onCallResultChange}
        />
      )}
    </div>
  )
}

function CallerInfoBox({
  call,
  className,
}: {
  call: ActiveCallSnapshot
  className?: string
}) {
  const formattedPhone = call.remote_phone ? formatPhoneForDisplay(call.remote_phone) : ""
  const displayName = call.contact_name ?? call.remote_display
  const label = displayName ?? (formattedPhone || "Unknown caller")
  const phone = call.remote_phone && displayName ? formattedPhone : ""
  const callLabel = callLabelFromSnapshot(call)
  const directionSubtitle =
    callLabel.variant === "list-dial"
      ? "List dialer call"
      : call.call_direction === "inbound"
        ? "Inbound call"
        : "Outbound call"
  const href = call.contact_id ? `/contact/${encodeURIComponent(call.contact_id)}` : null
  const content = (
    <>
      <UserRound className="ml-3.5 size-6 shrink-0 text-slate-500" />
      <div className="min-w-0 leading-tight">
        <p className="truncate text-sm font-semibold">{label}</p>
        <p className="truncate text-xs font-bold text-slate-600">
          {phone || directionSubtitle}
        </p>
      </div>
    </>
  )

  const sharedClassName = cn(
    "flex min-h-12 items-center rounded-sm border border-slate-200 bg-white text-slate-950 shadow-sm",
    href && "transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/30",
    barGap,
    className
  )

  if (href) {
    return (
      <Link
        href={href}
        aria-label={`Open contact record for ${label}`}
        className={sharedClassName}
      >
        {content}
      </Link>
    )
  }

  return <div className={sharedClassName}>{content}</div>
}

function NowCallingKeypadButton({
  action,
  disabled,
  onDigit,
}: {
  action: (typeof callingActions)[number]
  disabled?: boolean
  onDigit: (digit: string) => void
}) {
  const [open, setOpen] = useState(false)
  const Icon = action.icon

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Keypad"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            actionButtonClass,
            action.className,
            disabled && "pointer-events-none opacity-40",
            open &&
              "ring-2 ring-emerald-400/70 ring-offset-1 ring-offset-slate-100 brightness-105"
          )}
        >
          {Icon ? <Icon className="size-4" /> : null}
          {action.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        side="top"
        sideOffset={8}
        collisionPadding={12}
        className={nowCallingPopoverContentClass}
      >
        <div className="relative">
          <div
            className={cn(
              keypadPanelWidthClass,
              "rounded-l-xl border border-r-0 border-slate-300 p-3 shadow-[0_10px_38px_rgb(15_23_42/0.18)]",
              nowCallingSurfaceClass
            )}
          >
            <TelephoneKeypad onDigitPress={onDigit} />
          </div>
          <NowCallingPopoverHandle
            closeLabel="Close keypad"
            onClose={() => setOpen(false)}
            className="absolute left-full top-0 h-full rounded-tr-xl rounded-br-xl border-y border-r border-slate-300"
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function CallBarHandle({
  collapsed,
  spansDetailRow,
  onToggle,
  onDragPointerDown,
  onDragPointerMove,
  onDragPointerUp,
}: {
  collapsed: boolean
  spansDetailRow: boolean
  onToggle: () => void
  onDragPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void
  onDragPointerMove?: (event: React.PointerEvent<HTMLDivElement>) => void
  onDragPointerUp?: (event: React.PointerEvent<HTMLDivElement>) => void
}) {
  return (
    <div
      className={cn(
        handleColumnClass,
        collapsed
          ? "col-start-2 row-start-1 -mt-3 rounded-tr-md rounded-b-none rounded-br-none"
          : cn(
              "col-start-3 row-start-1",
              spansDetailRow && "row-span-2",
              spansDetailRow
                ? "-my-3 self-stretch rounded-r-md"
                : "-my-3 h-[calc(100%+1.5rem)] rounded-r-md"
            )
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? "Expand Now Calling bar" : "Collapse Now Calling bar"}
        aria-expanded={!collapsed}
        className={handleColumnButtonClass}
      >
        {collapsed ? (
          <ArrowUpRight className="size-4" strokeWidth={2.25} />
        ) : (
          <Menu className="size-4" strokeWidth={2.25} />
        )}
      </button>
      <div
        role="presentation"
        aria-label="Drag Now Calling bar horizontally"
        className="min-h-0 flex-1 cursor-grab touch-none active:cursor-grabbing"
        onPointerDown={onDragPointerDown}
        onPointerMove={onDragPointerMove}
        onPointerUp={onDragPointerUp}
        onPointerCancel={onDragPointerUp}
      />
    </div>
  )
}

export function NowCallingOverlay({
  showCallDetailsRow = DEFAULT_SHOW_CALL_DETAILS_ROW,
  callDetails: callDetailsOverride,
}: NowCallingOverlayProps = {}) {
  const {
    activeCall,
    calls,
    systemCall,
    selectedCallId,
    selectCall,
    controlPending,
    controlError,
    runCallControl,
  } = useActiveCall()
  const displayCall = activeCall ?? systemCall
  const isListDial =
    displayCall?.call_mode === "list_dial" ||
    (displayCall?.system_request_type ?? "").toString().toLowerCase() === "list_dial"
  const callDetails = displayCall
    ? callDetailsOverride ?? callDetailsFromActiveCall(displayCall)
    : callDetailsOverride ?? defaultCallDetails
  const [collapsed, setCollapsed] = useState(false)
  const showDetailsRow = showCallDetailsRow && !collapsed
  const [expandedCenterPercent, setExpandedCenterPercent] = useState(
    DEFAULT_EXPANDED_CENTER_PERCENT,
  )
  const [collapsedLeftPercent, setCollapsedLeftPercent] = useState(
    DEFAULT_COLLAPSED_LEFT_PERCENT,
  )
  const [dragging, setDragging] = useState(false)
  const [callResult, setCallResult] = useState<DialDispositionId | "">("")
  const [resultSaveError, setResultSaveError] = useState<string | null>(null)
  const [resultDialogOpen, setResultDialogOpen] = useState(false)
  const [resultDialogSnapshot, setResultDialogSnapshot] =
    useState<CallResultDialogSnapshot | null>(null)
  const prevDisplayCallRef = useRef<ActiveCallSnapshot | null>(null)
  const callWasBridgedRef = useRef(false)
  const resultSavedRef = useRef(false)
  const displayCallKeyRef = useRef<string | null>(null)
  const barRef = useRef<HTMLElement>(null)
  const dragRef = useRef<{
    startX: number
    startExpandedCenterPercent: number
    startCollapsedLeftPercent: number
  } | null>(null)

  const handleDragPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragRef.current = {
        startX: event.clientX,
        startExpandedCenterPercent: expandedCenterPercent,
        startCollapsedLeftPercent: collapsedLeftPercent,
      }
      setDragging(true)
    },
    [collapsedLeftPercent, expandedCenterPercent],
  )

  const handleDragPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return
      const barWidth = barRef.current?.offsetWidth ?? 0
      const vw = window.innerWidth
      if (vw <= 0) return
      const deltaX = event.clientX - dragRef.current.startX
      const deltaPercent = (deltaX / vw) * 100

      if (collapsed) {
        const next = dragRef.current.startCollapsedLeftPercent + deltaPercent
        setCollapsedLeftPercent(clampCollapsedLeftPercent(next, barWidth))
      } else {
        const next = dragRef.current.startExpandedCenterPercent + deltaPercent
        setExpandedCenterPercent(clampExpandedCenterPercent(next, barWidth))
      }
    },
    [collapsed],
  )

  const handleDragPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return
      dragRef.current = null
      setDragging(false)
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    [],
  )

  const displayCallKey = displayCall?.call_id ?? displayCall?.request_ulid ?? null
  displayCallKeyRef.current = displayCallKey

  useEffect(() => {
    if (displayCallKey) {
      setCallResult("")
      setResultSaveError(null)
      resultSavedRef.current = false
      callWasBridgedRef.current = false
    }
  }, [displayCallKey])

  useEffect(() => {
    if (displayCall?.bridged) {
      callWasBridgedRef.current = true
    }
  }, [displayCall?.bridged])

  useEffect(() => {
    const prev = prevDisplayCallRef.current
    prevDisplayCallRef.current = displayCall ?? null

    if (prev && !displayCall) {
      const wasListDial =
        prev.call_mode === "list_dial" ||
        (prev.system_request_type ?? "").toString().toLowerCase() === "list_dial"
      const wasBridged = callWasBridgedRef.current || Boolean(prev.bridged)
      callWasBridgedRef.current = false
      if (!wasListDial && wasBridged && !resultSavedRef.current) {
        setResultDialogSnapshot({
          contactId: prev.contact_id ?? null,
          contactName: prev.contact_name ?? prev.remote_display ?? null,
          remotePhone: prev.remote_phone ?? null,
          callId: prev.call_id ?? null,
        })
        setResultDialogOpen(true)
      }
    }
  }, [displayCall])

  const handleCallResultChange = useCallback(
    async (next: DialDispositionId | "") => {
      setCallResult(next)
      setResultSaveError(null)
      if (!next) return

      const contactId = displayCall?.contact_id?.trim() ?? ""
      const savedFor = displayCall?.call_id ?? displayCall?.request_ulid ?? null
      if (!contactId) {
        resultSavedRef.current = false
        return
      }

      try {
        const res = await CommandPage(CONTACT_COMMAND_PATH).recordCallDisposition({
          contact_id: contactId,
          call_id: displayCall?.call_id || undefined,
          disposition: next,
        })
        if (displayCallKeyRef.current !== savedFor) return
        if (!res.ok) {
          resultSavedRef.current = false
          setResultSaveError(res.error ?? "Could not save call result")
          return
        }
        resultSavedRef.current = true
      } catch (error) {
        if (displayCallKeyRef.current !== savedFor) return
        resultSavedRef.current = false
        setResultSaveError(error instanceof Error ? error.message : "Could not save call result")
      }
    },
    [displayCall],
  )

  useEffect(() => {
    function reclampFromStoredPercent() {
      const barWidth = barRef.current?.offsetWidth ?? 0
      if (barWidth <= 0) return
      setExpandedCenterPercent((prev) =>
        clampExpandedCenterPercent(prev, barWidth),
      )
      setCollapsedLeftPercent((prev) => clampCollapsedLeftPercent(prev, barWidth))
    }

    reclampFromStoredPercent()
    window.addEventListener("resize", reclampFromStoredPercent)
    return () => window.removeEventListener("resize", reclampFromStoredPercent)
  }, [collapsed])

  const actionEnabled = useCallback(
    (action: ActiveCallControlAction) =>
      Boolean(displayCall?.actions_enabled?.[action]),
    [displayCall],
  )
  const extraCalls = calls.filter((call) => {
    const key = call.call_id ?? call.request_ulid
    return key && key !== selectedCallId
  })

  if (!displayCall) {
    return (
      <>
        <div aria-hidden className="pointer-events-none fixed inset-0 z-50 bg-transparent" />
        <CallResultDialog
          open={resultDialogOpen}
          onOpenChange={setResultDialogOpen}
          snapshot={resultDialogSnapshot}
        />
      </>
    )
  }

  const barPositionStyle = collapsed
    ? {
        left: `${collapsedLeftPercent}%`,
        bottom: 0,
        transform: undefined,
      }
    : {
        left: `${expandedCenterPercent}%`,
        bottom: OVERLAY_PADDING_PX,
        transform: "translateX(-50%)",
      }

  return (
    <>
      <div
        aria-hidden={false}
        className="pointer-events-none fixed inset-0 z-50 bg-transparent"
      >
      <section
        ref={barRef}
        aria-label={`Now calling ${displayCall.contact_name ?? displayCall.remote_display ?? displayCall.remote_phone ?? "active call"}`}
        style={barPositionStyle}
        className={cn(
          "pointer-events-auto fixed z-50 grid items-stretch overflow-hidden border shadow-[0_12px_35px_rgb(15_23_42/0.14)] backdrop-blur-sm",
          nowCallingSurfaceClass,
          !dragging && "transition-[width,left] duration-200",
          barGap,
          collapsed
            ? cn(
                "w-fit rounded-t-md rounded-b-none border-b-0",
                barPaddingCollapsed,
                handleGridColsCollapsed
              )
            : cn(
                "rounded-md",
                barPaddingNoRight,
                isListDial ? EXPANDED_BAR_WIDTH_LIST_DIAL_CLASS : EXPANDED_BAR_WIDTH_CLASS,
                "max-w-[calc(100vw-3rem)]",
                handleGridColsExpanded,
                showDetailsRow && "grid-rows-[auto_auto]"
              )
        )}
      >
        {collapsed ? (
          <div className="col-start-1 row-start-1 min-w-0 pb-3.5">
            <CallerInfoBox call={displayCall} />
          </div>
        ) : (
          <CallerInfoBox call={displayCall} className="col-start-1 row-start-1" />
        )}

        {!collapsed ? (
          <div
            className={cn(
              "col-start-2 row-start-1 grid items-stretch",
              isListDial ? "grid-cols-6" : "grid-cols-5",
              barGap
            )}
          >
            {callingActions.map((action) => {
              if (action.keypad) {
                const disabled = !actionEnabled("send_dtmf") || controlPending !== null
                return (
                  <NowCallingKeypadButton
                    key={action.label}
                    action={action}
                    disabled={disabled}
                    onDigit={(digit) => void runCallControl("send_dtmf", { digit })}
                  />
                )
              }

              if (action.transfer) {
                const disabled =
                  !actionEnabled("transfer_blind") || controlPending !== null
                return (
                  <NowCallingTransferButton
                    key={action.label}
                    action={action}
                    actionButtonClass={actionButtonClass}
                    disabled={disabled}
                    onTransfer={(target: NowCallingTransferRequest) => {
                      void runCallControl("transfer_blind", {
                        destination_type: target.destination_type,
                        destination_number: target.destination_number,
                        destination_id: target.destination_id,
                      })
                    }}
                  />
                )
              }

              const Icon = action.icon
              const controlAction: ActiveCallControlAction | null =
                action.label === "Hold"
                  ? "hold"
                  : action.park
                    ? "park"
                    : action.label === "Hang Up"
                      ? "hangup"
                      : null
              const pendingThis =
                (action.label === "Hold" && controlPending === "hold") ||
                (action.label === "Park" && controlPending === "park") ||
                (action.label === "Hang Up" && controlPending === "hangup")
              const disabled =
                !controlAction || !actionEnabled(controlAction) || controlPending !== null || pendingThis

              const hangUpButton = (
                <button
                  key={action.label}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (action.label === "Hold") {
                      void runCallControl("hold")
                      return
                    }
                    if (action.park) {
                      void runCallControl("park")
                      return
                    }
                    if (action.label === "Hang Up") {
                      void runCallControl("hangup")
                    }
                  }}
                  className={cn(
                    actionButtonClass,
                    action.className,
                    disabled && "pointer-events-none opacity-40"
                  )}
                >
                  {action.park ? (
                    <span className="flex size-4 items-center justify-center rounded-sm border border-violet-400 text-[10px] font-semibold leading-none">
                      P
                    </span>
                  ) : Icon ? (
                    <Icon className="size-4" />
                  ) : null}
                  {action.label}
                </button>
              )

              if (action.label === "Hang Up" && isListDial) {
                const dropVmDisabled =
                  !actionEnabled("drop_voicemail") ||
                  controlPending !== null ||
                  controlPending === "drop_voicemail"
                return (
                  <Fragment key="drop-voicemail-hangup">
                    <button
                      type="button"
                      disabled={dropVmDisabled}
                      onClick={() => void runCallControl("drop_voicemail")}
                      className={cn(
                        actionButtonClass,
                        "border-orange-300 from-orange-50 via-orange-100 to-orange-200 text-orange-700 shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_0_rgb(254_215_170/0.6)]",
                        dropVmDisabled && "pointer-events-none opacity-40"
                      )}
                    >
                      <Voicemail className="size-4" />
                      Drop Voicemail
                    </button>
                    {hangUpButton}
                  </Fragment>
                )
              }

              return hangUpButton
            })}
          </div>
        ) : null}

        {showDetailsRow ? (
          <CallDetailsRow
            details={callDetails}
            callResult={callResult}
            resultError={resultSaveError}
            onCallResultChange={(value) => {
              void handleCallResultChange(value)
            }}
          />
        ) : null}

        {!collapsed && extraCalls.length > 0 ? (
          <div className="col-span-2 col-start-1 row-start-3 flex flex-wrap items-center gap-2 border-t border-slate-300/90 px-1 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Other calls
            </span>
            {extraCalls.map((call) => {
              const key = call.call_id ?? call.request_ulid ?? call.remote_display ?? "call"
              const label =
                call.contact_name ??
                call.remote_display ??
                call.remote_phone ??
                "Active call"
              const selected = key === selectedCallId
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (call.call_id) selectCall(call.call_id)
                    else if (call.request_ulid) selectCall(call.request_ulid)
                  }}
                  className={cn(
                    "max-w-[10rem] truncate rounded-full border px-2.5 py-1 text-[11px] font-semibold shadow-sm transition-colors",
                    selected
                      ? "border-slate-400 bg-slate-200 text-slate-900"
                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>
        ) : null}

        {controlError ? (
          <p className="col-span-2 col-start-1 px-1 pb-1 text-[11px] font-medium text-red-600">
            {controlError}
          </p>
        ) : null}

        <CallBarHandle
          collapsed={collapsed}
          spansDetailRow={showDetailsRow}
          onToggle={() => setCollapsed((value) => !value)}
          onDragPointerDown={handleDragPointerDown}
          onDragPointerMove={handleDragPointerMove}
          onDragPointerUp={handleDragPointerUp}
        />
      </section>
      </div>
      <CallResultDialog
        open={resultDialogOpen}
        onOpenChange={setResultDialogOpen}
        snapshot={resultDialogSnapshot}
      />
    </>
  )
}
